package com.vizio.redwolf.pay.api

import com.vizio.redwolf.pay.client.AccountRewardsHttpClient
import com.vizio.redwolf.pay.model.RewardsRequestInfo
import com.vizio.redwolf.pay.model.RewardsResponse
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.ECommerceEnvironmentConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.TimeoutException
import com.vizio.redwolf.utils.choosePayEnvironment
import com.vizio.redwolf.utils.clientprovider.ClientConfigFactory
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import com.vizio.redwolf.utils.model.PayFailureResponse
import com.vizio.redwolf.utils.retryRequest
import io.ktor.client.call.body
import io.ktor.client.statement.HttpResponse
import io.ktor.http.isSuccess
import kotlin.js.JsExport

class AccountRewardsApi(environment: PayEnvironment, private val appConfig: AppConfig, private val clientConfig: ClientConfig = ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS)) :
    AccountRewardsApiInterface {

    private val accountRewardsClient = AccountRewardsHttpClient(environment, appConfig, clientConfig)

    constructor(envConfig: ECommerceEnvironmentConfig, appConfig: AppConfig) : this(choosePayEnvironment(envConfig), appConfig)

    constructor(envConfig: ECommerceEnvironmentConfig, appConfig: AppConfig, clientConfig: ClientConfig) : this(choosePayEnvironment(envConfig), appConfig, clientConfig)

    constructor(envConfig: ECommerceEnvironmentConfig, appConfig: AppConfig, timeout: Long) : this(choosePayEnvironment(envConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS, timeout))

    override suspend fun requestRewards(requestInfo: RewardsRequestInfo): AccountRewardsResult<RewardsResponse> {
        return retryAccountRewardsRequest {
            val result = accountRewardsClient.requestRewards(requestInfo)
            handleAccountRewardsHttpResult(result)
        }
    }

    private suspend inline fun <T : HttpResponse, reified R : Any> handleAccountRewardsHttpResult(httpResult: HttpResult<T>): AccountRewardsResult<R> {
        return when (httpResult) {
            is HttpResult.Success -> {
                val httpResponse: HttpResponse = httpResult.data
                val statusCode = httpResponse.status.value
                val headers = ResponseHeaders.Valid(statusCode)
                if (httpResponse.status.isSuccess()) {
                    try {
                        AccountRewardsResult.Success(headers, httpResponse.body())
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        AccountRewardsResult.Error(headers, ex)
                    }
                } else {
                    try {
                        val failureMessage = httpResponse.body<PayFailureResponse>()
                        AccountRewardsResult.Failed(headers, response = failureMessage)
                    } catch (ex: Exception) {
                        ex.printStackTrace()
                        AccountRewardsResult.Error(headers, ex)
                    }
                }
            }
            is HttpResult.Error -> {
                val headers = ResponseHeaders.Invalid
                val exception = httpResult.exception
                if (exception is TimeoutException) {
                    AccountRewardsResult.Timeout(headers, exception)
                }
                else {
                    AccountRewardsResult.Error(ResponseHeaders.Invalid, exception)
                }
            }
        }
    }

    private fun <T : Any> isAccountRewardsSuccess(result: T): Boolean {
        return result is AccountRewardsResult<*>
    }

    private suspend inline fun <T: Any> retryAccountRewardsRequest(block: () -> T): T {
        return retryRequest(clientConfig, block, isSuccess = ::isAccountRewardsSuccess)
    }
}

@JsExport
sealed class AccountRewardsResult<out T : Any> {
    data class Success<out T : Any>(val headers: ResponseHeaders, val data: T) : AccountRewardsResult<T>()
    data class Failed(val headers: ResponseHeaders, val response: PayFailureResponse) : AccountRewardsResult<Nothing>()
    data class Error(val headers: ResponseHeaders, val exception: Throwable) : AccountRewardsResult<Nothing>()
    data class Timeout(val headers: ResponseHeaders, val exception: Throwable) : AccountRewardsResult<Nothing>()

    fun <R> fold(
        onSuccess: (success: Success<T>) -> R,
        onFailed: (failed: Failed) -> R,
        onError: (error: Error) -> R,
        onTimeout: (timeout: Timeout) -> R
    ): R {
        return when (this) {
            is Success -> onSuccess(Success(this.headers, this.data))
            is Failed -> onFailed(Failed(this.headers, this.response))
            is Error -> onError(Error(this.headers, this.exception))
            is Timeout -> onTimeout(Timeout(this.headers, this.exception))
        }
    }
}